import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import singleCover from "assets/singleCover.png";
import ALBUMCover from "assets/epCover.png";
import epCover from "assets/ep.png";
import albumCover from "assets/albumCover.png";
import musicVideoCover from "assets/musicVideoCover.png";
import ringtoneCover from "assets/ringtoneCover.png";
import PlatFormModal from "./PlatFormModal";
import { PlatFormUserInfoContentProps } from "../types";
import { useNavigate, useParams } from "react-router";
import { Plan } from "types/release.interface";
import { FormattedMessage } from "react-intl";
import IntlStore from "stories/IntlStore";
import { InfoTooltip } from "ui-kit/Tooltip/InfoTooltip";
import { useRelease } from "components/NewRelease/hooks/useRelease";
import { ProgressSpinner } from "primereact/progressspinner";
import { showToast } from "components/ui-kit/Toast";
import { API_BASE_URL, API_URLS, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";
import axios from "axios";

const plans: Plan[] = [
    {
        label: IntlStore.intl.formatMessage({
            id: "NewRelease.Platform.PlanSingle",
            defaultMessage: "Single [1–2 tracks]"
        }),
        price: 9.99,
        text: "one time",
        image: singleCover,
        active: false,
        type: "SINGLE"
    },
    {
        label: IntlStore.intl.formatMessage({
            id: "NewRelease.Platform.PlanEP",
            defaultMessage: "EP [3–6 tracks]"
        }),
        price: 14.99,
        text: "one time",
        image: epCover,
        active: false,
        type: "EP"
    },
    {
        label: IntlStore.intl.formatMessage({
            id: "NewRelease.Platform.PlanAlbum",
            defaultMessage: "Album [7–12 tracks]"
        }),
        price: 24.99,
        text: "one-time payment",
        image: ALBUMCover,
        active: false,
        type: "ALBUM"
    },
    {
        label: IntlStore.intl.formatMessage({
            id: "NewRelease.Platform.PlanMusicVideo",
            defaultMessage: "Music video"
        }),
        price: 14.99,
        text: "one time",
        image: albumCover,
        active: false,
        type: "CLIP"
    },
    {
        label: IntlStore.intl.formatMessage({
            id: "NewRelease.Platform.PlanTikTokSound",
            defaultMessage: "Tik Tok sound"
        }),
        price: 4.99,
        text: "one time",
        image: musicVideoCover,
        active: false,
        type: "TIKTOK_SOUND"
    },
    {
        label: IntlStore.intl.formatMessage({
            id: "NewRelease.Platform.PlanRingtone",
            defaultMessage: "Ringtone"
        }),
        price: 4.99,
        text: "one time",
        image: ringtoneCover,
        active: false,
        type: "RINGTONE"
    }
];

const FinalPricing: React.FC<PlatFormUserInfoContentProps> = ({
    step,
    setStep,
    link,
    releaseType,
    tracksLength,
    paid,
    hasUgc,
    deliveryId,
    setDeliveryId,
    releases
}) => {
    const navigate = useNavigate();
    const { releasePay, getCheckout, updating, release, validateRelease } = useRelease()


    // Keep local planType for UI highlighting, but prefer server-driven ids/options from checkout response.
    const [planType, setPlanType] = useState<string>(releaseType);
    const [selectedReleaseTypeId, setSelectedReleaseTypeId] = useState<number | null>(null);
    const [pendingType, setPendingType] = useState<string | null>(null);
    const [isModalOpen, setModalOpen] = useState<boolean>(false);
    const [boostUGC] = useState(true);
    const [agreed, setAgreed] = useState(false);
    const [plansState] = useState<Plan[]>(plans);
    const [delivery, setDelivery] = useState<number>(19.99);
    const [checkoutData, setCheckoutData] = useState<any>(null);
    const [checkoutRefreshing, setCheckoutRefreshing] = useState(false);
    const [validationPassed, setValidationPassed] = useState(false);
    const [validationPending, setValidationPending] = useState(false);
    const validatedReleaseIdRef = React.useRef<string | null>(null);
    const lastCheckoutErrorKeyRef = React.useRef<string | null>(null);
    const { id } = useParams()
    const confirmTypes = useMemo(
        () => new Set(["ALBUM", "SINGLE", "CLIP", "TIKTOK_SOUND", "RINGTONE"]),
        []
    );

    // Calculate days until release date
    const daysUntilRelease = useMemo(() => {
        const releaseDateStr = releases?.releases?.data?.release_date;
        if (!releaseDateStr) return null;
        
        const releaseDate = new Date(releaseDateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        releaseDate.setHours(0, 0, 0, 0);
        
        const diffTime = releaseDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays;
    }, [releases?.releases?.data?.release_date]);

    // Determine which delivery options are available:
    // Express (24h): needs at least 1 day
    // Fast (1 week): needs at least 7 days
    // Standard (3 weeks): needs at least 21 days
    const isExpressAvailable = useMemo(() => {
        return daysUntilRelease !== null && daysUntilRelease >= 1;
    }, [daysUntilRelease]);

    const isFastAvailable = useMemo(() => {
        return daysUntilRelease !== null && daysUntilRelease >= 7;
    }, [daysUntilRelease]);

    const isStandardAvailable = useMemo(() => {
        return daysUntilRelease !== null && daysUntilRelease >= 21;
    }, [daysUntilRelease]);

    const deliveryAvailabilityById = useMemo(() => {
        // Backend ids in this app: 1=Express (24h), 2=Fast (1 week), 3=Standard (3 weeks)
        return {
            1: isExpressAvailable,
            2: isFastAvailable,
            3: isStandardAvailable,
        } as Record<number, boolean>;
    }, [isExpressAvailable, isFastAvailable, isStandardAvailable]);

    // Auto-select the first available option if current selection is not available
    // Order preference: most expensive first (Express -> Fast -> Standard)
    useEffect(() => {
        if (daysUntilRelease === null) return;
        const currentOk = deliveryAvailabilityById[Number(deliveryId)] ?? true;
        if (currentOk) return;

        const nextId = [1, 2, 3].find((optId) => deliveryAvailabilityById[optId]);
        if (!nextId) return;

        setDeliveryId(nextId);
        // fallback local price if server options not loaded yet
        setDelivery(nextId === 1 ? 19.99 : nextId === 2 ? 9.99 : 0);
    }, [daysUntilRelease, deliveryId, deliveryAvailabilityById, setDeliveryId]);

    const price = useMemo(() => {
        const plan = plansState.find(p => p.type === planType);
        const dilUgc = hasUgc ? delivery + 4.99 : delivery;
        return plan ? Number((plan.price + dilUgc

        ).toFixed(2)) : 0;
    }, [plansState, planType, delivery]);

    const releaseTypeIdForCheckout = useMemo(() => {
        if (selectedReleaseTypeId) return selectedReleaseTypeId;
        const map: Record<string, number> = {
            ALBUM: 1,
            SINGLE: 2,
            EP: 3,
            CLIP: 5, // API may use 5 for CLIP (see checkout response)
            PODCAST: 4,
            RINGTONE: 6,
            TIKTOK_SOUND: 7,
        };
        return map[String(planType || "").toUpperCase()] ?? 0;
    }, [planType, selectedReleaseTypeId]);

    // Validate release before allowing checkout/pay
    useEffect(() => {
        if (!id) return;
        // Ждём, пока подтянутся основные данные релиза (releases.releases.data)
        if (!releases?.releases?.data) return;

        const rid = String(id);
        // Если уже валидировали этот релиз – повторно не ходим на бэк (в т.ч. при double-invoke эффекта в StrictMode).
        if (validatedReleaseIdRef.current === rid) return;
        // Помечаем сразу, до await, чтобы даже при двойном вызове эффекта запрос ушёл один раз.
        validatedReleaseIdRef.current = rid;

        (async () => {
            try {
                setValidationPending(true);
                const data: any = await validateRelease(Number(rid));
                if (process.env.NODE_ENV !== "production") {
                    // eslint-disable-next-line no-console
                    console.log("[FinalPricing] validateRelease result", data);
                }

                // Новый формат: { success: true, data: { status: "ok", errors: [] } }
                const successFlag = data?.success === true;
                const statusFlag =
                  data?.data?.status === "ok" ||
                  data?.status === "ok" ||
                  data?.data?.status === true ||
                  data?.status === true;
                const ok = successFlag && statusFlag;

                if (!ok) {
                    const rawErrors = data?.data?.errors ?? data?.errors;
                    const errors: any[] = Array.isArray(rawErrors) ? rawErrors : [];
                    if (errors.length) {
                        errors
                            .filter((e) => e != null)
                            .forEach((e) => {
                                const msg = typeof e === "string" ? e : JSON.stringify(e);
                                // Sticky toast с заголовком и текстом ошибки
                                showToast(`Fix these errors:\n${msg}`, 0, "error");
                            });
                    } else if (data?.message) {
                        showToast(`Fix these errors:\n${String(data.message)}`, 0, "error");
                    }
                }
                setValidationPassed(ok);
            } catch (e) {
                setValidationPassed(false);
                if (e instanceof Error && e.message) {
                    showToast(`Fix these errors:\n${e.message}`, 0, "error");
                }
            } finally {
                setValidationPending(false);
            }
        })();
        // validateRelease — стабильный useCallback; намеренно не включаем его в зависимости,
        // чтобы эффект не перезапускался лишний раз
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, releases?.releases?.data]);

    // Fetch checkout data from server (price/options should be server-calculated)
    useEffect(() => {
        if (!id) return;
        if (!releaseTypeIdForCheckout) return;
        (async () => {
            try {
                // Silent fetch: do not block the whole FinalPricing block when user changes delivery.
                // We still want to reflect "price recalculating" in the Pay button.
                setCheckoutRefreshing(true);
                const data = await getCheckout(Number(id), releaseTypeIdForCheckout, deliveryId ?? null, { silent: true });
                setCheckoutData(data);
                lastCheckoutErrorKeyRef.current = null;
            } catch (e: any) {
                // fallback to local price
                setCheckoutData(null);

                // Log full backend error for debugging why Pay is disabled
                // (e.g. validation / decision from checkout API).
                // This will show both the JS error and raw response from backend in console.
                // eslint-disable-next-line no-console
                console.error(
                  "[FinalPricing.getCheckout] checkout error",
                  e,
                  e?.response?.data ?? null
                );

                const backendMessage =
                  e?.response?.data?.message ??
                  e?.response?.data?.error ??
                  e?.message ??
                  "Failed to load checkout data";

                // avoid spamming toast on re-renders with same failing params
                const key = `${id}|${releaseTypeIdForCheckout}|${deliveryId ?? "null"}`;
                if (lastCheckoutErrorKeyRef.current !== key) {
                    lastCheckoutErrorKeyRef.current = key;
                    showToast(backendMessage, 4000, "error");
                }
            } finally {
                setCheckoutRefreshing(false);
            }
        })();
    }, [id, deliveryId, releaseTypeIdForCheckout]);

    const checkout = checkoutData?.data ?? checkoutData?.data?.data ?? checkoutData?.data ?? null;
    const serverDeliveryOptions = (checkout?.delivery_options ?? []) as any[];
    const serverSurcharges = (checkout?.surcharges ?? []) as any[];
    const serverLineItems = (checkout?.line_items ?? []) as any[];
    const serverNotice = checkout?.notice ?? checkout?.decision?.notice ?? null;
    const canCheckoutBackend = checkout?.can_checkout ?? checkout?.decision?.can_checkout ?? true;
    const canCheckout = canCheckoutBackend && validationPassed;

    const sortedServerDeliveryOptions = useMemo(() => {
        const list = Array.isArray(serverDeliveryOptions) ? [...serverDeliveryOptions] : [];
        // Expensive first: sort by price_cents desc (fallback to price)
        list.sort((a: any, b: any) => {
            const ap = Number(a?.price_cents ?? (a?.price != null ? Number(a.price) * 100 : 0));
            const bp = Number(b?.price_cents ?? (b?.price != null ? Number(b.price) * 100 : 0));
            return bp - ap;
        });
        return list;
    }, [serverDeliveryOptions]);

    const serverTotalPrice = useMemo(() => {
        // try common shapes
        const v =
            checkoutData?.data?.total ??
            checkoutData?.data?.amount ??
            checkoutData?.total ??
            checkoutData?.amount ??
            null;
        const n = typeof v === "string" ? Number(v) : v;
        return Number.isFinite(n) ? Number(n) : null;
    }, [checkoutData]);

    const serverTotalText = useMemo(() => {
        const v = checkout?.total ?? checkoutData?.data?.total ?? null;
        return v != null ? String(v) : null;
    }, [checkout, checkoutData]);

    const serverCurrency = useMemo(() => {
        const v = checkout?.currency ?? checkoutData?.data?.currency ?? "usd";
        return String(v || "usd").toUpperCase();
    }, [checkout, checkoutData]);

    // Debug: log why the Pay button is disabled/enabled
    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log("[FinalPricing] Pay button state", {
            agreed,
            canCheckoutBackend,
            validationPassed,
            canCheckout,
            checkoutRefreshing,
            disabled: !agreed || !canCheckout || checkoutRefreshing,
            checkout,
        });
    }, [agreed, canCheckoutBackend, validationPassed, canCheckout, checkoutRefreshing, checkout]);

    // Sync local selections from server response (handles auto-switch on track count)
    useEffect(() => {
        if (!checkout) return;
        const selTypeId = checkout?.selected_release_type_id ?? checkout?.final_release_type_id ?? null;
        const selDeliveryId = checkout?.selected_delivery_option_id ?? null;
        if (selTypeId != null) setSelectedReleaseTypeId(Number(selTypeId));
        if (selDeliveryId != null && Number(selDeliveryId) !== Number(deliveryId)) {
            setDeliveryId(Number(selDeliveryId));
            const found = serverDeliveryOptions.find((d: any) => Number(d?.id) === Number(selDeliveryId));
            if (found?.price != null) setDelivery(Number(found.price));
        }
        // план (тип) берём не из checkout.release_type_options, а из текущего releaseType/planType маппинга
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoutData]);


    useEffect(() => {
        setPlanType(releaseType);
    }, [releaseType]);

    useEffect(() => {
        if (step === 6) setModalOpen(false);
    }, [step]);

    const handleRedirect = () => navigate(link);

    const handlePlanClick = (nextType: string) => {
        if (nextType === planType) return;

        if (!confirmTypes.has(nextType)) {
            setPlanType(nextType);
            setPendingType(null);
            setModalOpen(false);
            return;
        }

        setPendingType(nextType);
        setModalOpen(true);
    };

    const handleConfirmSwitch = () => {
        if (pendingType) {
            setPlanType(pendingType);
            setPendingType(null);
        }
        setModalOpen(false);
        navigate(`/releases/new-release/${id}?step=track-upload`)
    };

    const handlePay = async () => {
        if (!agreed) return;

        try {
            // Create Stripe checkout session; redirect handled inside releasePay
            await releasePay(Number(id), deliveryId, releaseTypeIdForCheckout);

        } catch (error) {
            console.error("Payment error:", error);
            showToast((error as any)?.message || "Payment error", 4000, "error");
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const stepParam = params.get("step");

        if (stepParam === "paidPlatform" || paid) {
            setStep(6);
        }
    }, [location.search, setStep, paid]);


    return (

        <Container>
            {updating && (
                <BlockLoadingOverlay>
                    <ProgressSpinner />
                </BlockLoadingOverlay>
            )}
            <>
                    {serverNotice?.message && (
                        <NoticeBox>
                            <NoticeTitle>{String(serverNotice.code || "notice")}</NoticeTitle>
                            <NoticeText>{String(serverNotice.message)}</NoticeText>
                        </NoticeBox>
                    )}
                    <PlansGrid>
                        {plansState.map((p) => {
                            const type = String(p.type || "");
                                const isSelected =
                                type &&
                                String(planType || "").toUpperCase() === type.toUpperCase();
                            const canChangePlan = !paid && step < 6;

                                return (
                                <PlanCard
                                    key={type || p.label}
                                        selected={!!isSelected}
                                        onClick={() => {
                                        if (!canChangePlan) return;
                                            if (type) handlePlanClick(type);
                                        }}
                                    background={p.image as any}
                                    style={{
                                        opacity: isSelected ? 1 : 0.7,
                                        cursor: canChangePlan ? "pointer" : "default",
                                    }}
                                        >
                                            <Overlay />
                                            <div>
                                        {p.label}
                                        <span>{`${p.price}$`}</span>
                                        <PlanText>{p.text}</PlanText>
                                            </div>
                                        </PlanCard>
                                );
                            })}
                    </PlansGrid>

                    <Section>
                        <Title>
                            {step !== 6 ? (
                                <FormattedMessage
                                    id="NewRelease.Platform.AdditionalServices"
                                    defaultMessage="Additional services"
                                />
                            ) : (
                                <FormattedMessage
                                    id="NewRelease.Platform.AdditionalServicesDone"
                                    defaultMessage="Paid additional services"
                                />
                            )}{" "}
                            {step !== 6 && (
                                <EditButton onClick={handleRedirect}>
                                    <FormattedMessage id="NewRelease.Platform.Edit" defaultMessage="Edit" />
                                </EditButton>
                            )}
                        </Title>

                        {/* Surcharges should be rendered from server checkout response */}
                        {Array.isArray(serverSurcharges) && serverSurcharges.length > 0 && (
                            <SurchargesList>
                                {serverSurcharges.map((s: any) => (
                                    <AdditionalService key={String(s?.id ?? s?.name ?? "")} style={{ paddingTop: "11px" }}>
                                        {String(s?.name ?? "Surcharge")}{" "}
                                <span>
                                            <InfoTooltip name={`surcharge-${String(s?.id ?? s?.name ?? "x")}`} tooltip={String(s?.description ?? "")} />{" "}
                                            {String(s?.price ?? (s?.price_cents != null ? (Number(s.price_cents) / 100).toFixed(2) : ""))}$
                                </span>
                            </AdditionalService>
                                ))}
                            </SurchargesList>
                        )}

                        <DeliveryTitle style={{ paddingTop: "10px" }}>
                            {step !== 6 && (
                                <>
                                    <FormattedMessage
                                        id="NewRelease.Platform.Delivery"
                                        defaultMessage="Delivery options"
                                    />
                                    <span>
                                        <InfoTooltip 
                                            name="platform-info-Delivery" 
                                            tooltip="Available delivery options depend on your release date. If you need a longer delivery option, please change your release date to allow more time for distribution." 
                                        />
                                    </span>
                                </>
                            )}
                        </DeliveryTitle>

                        <DeliveryBox>
                            {(sortedServerDeliveryOptions.length ? sortedServerDeliveryOptions : []).map((d: any) => {
                                const did = Number(d?.id);
                                // IMPORTANT: selection must be controlled by user choice immediately.
                                // Do NOT trust stale `is_selected` from the previous checkout response,
                                // otherwise two options can appear selected during refresh.
                                const isSel = Number(deliveryId) === did;
                                const priceText = d?.price != null ? String(d.price) : (d?.price_cents != null ? (Number(d.price_cents) / 100).toFixed(2) : "");
                                const name = String(d?.name ?? "Delivery");
                                const isAvailable = deliveryAvailabilityById[did] ?? true;
                                const disabled = !isAvailable;
                                const switchingLocked = checkoutRefreshing;
                                const interactionDisabled = disabled || switchingLocked;
                                return (
                                <Box 
                                        key={String(did)}
                                    onClick={() => { 
                                            // BUGFIX: while checkout is refreshing (Pay button loader),
                                            // don't allow rapid switching of delivery options.
                                            if (interactionDisabled) return;
                                            if (isSel) return;
                                            // Lock immediately to prevent double-tap before state updates.
                                            setCheckoutRefreshing(true);
                                            setDeliveryId(did);
                                            if (priceText) setDelivery(Number(priceText));
                                    }}
                                    style={{ 
                                            opacity: disabled ? 0.5 : (switchingLocked ? 0.7 : 1),
                                            cursor: disabled ? "not-allowed" : (switchingLocked ? "progress" : "pointer"),
                                            pointerEvents: switchingLocked ? "none" : "auto",
                                    }}
                                    aria-disabled={interactionDisabled}
                                >
                                    {step !== 6 && (
                                        <Round
                                            style={{
                                                    border: isSel ? "6px solid #6777FF" : "1px solid #6777FF",
                                                    opacity: disabled ? 0.5 : (switchingLocked ? 0.7 : 1)
                                            }}
                                        />
                                    )}
                                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                            {name}{" "}
                                        <span>
                                                <InfoTooltip name={`delivery-${did}`} tooltip={name} /> {priceText}$
                                        </span>
                                    </div>
                                </Box>
                                );
                            })}
                        </DeliveryBox>
                    </Section>

                    {step <= 5 && (
                        <Agreement>
                            <InputBox>
                                <Checkbox checked={agreed} onChange={e => setAgreed(e.target.checked)} />
                                <Text>
                                    <FormattedMessage
                                        id="NewRelease.Platform.Agreement1"
                                        defaultMessage="I accept the terms and conditions of the"
                                    />
                                </Text>
                                <PublicOff>
                                    <FormattedMessage
                                        id="NewRelease.Platform.Agreement2"
                                        defaultMessage="Public offer agreement"
                                    />
                                </PublicOff>
                            </InputBox>

                            <PayButton disabled={!agreed || !canCheckout || checkoutRefreshing} onClick={handlePay}>
                                <PayButtonInner>
                                    {checkoutRefreshing ? (
                                        <PaySpinnerWrap>
                                            <ProgressSpinner style={{ width: 18, height: 18 }} strokeWidth="8" />
                                        </PaySpinnerWrap>
                                    ) : (
                                        <span>
                                          Pay {serverTotalText ? `${serverTotalText} ${serverCurrency}` : (serverTotalPrice ?? price) ? `${(serverTotalPrice ?? price)} $` : " "}
                                        </span>
                                    )}
                                </PayButtonInner>
                            </PayButton>
                        </Agreement>
                    )}

                    <PlatFormModal
                        isOpen={isModalOpen}
                        onClose={() => {
                            setModalOpen(false);
                            setPendingType(null);
                        }}
                        onConfirm={handleConfirmSwitch}
                        step={step}
                        setStep={setStep}
                        releaseType={pendingType || planType}
                    />
                </>
        </Container>

    );
};

const Container = styled.div`
  position: relative;
  min-height: 180px;
`;

const Title = styled.span`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0;
  color: #00000080;
`;

const MainTitle = styled(Title)`
  color: #000000;
`;

const PlansGrid = styled.div`
  padding-top: 15px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 26px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div<{ selected: boolean; background: string }>`
  position: relative;
  height: 140px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background-image: url(${({ background }) => background});
  background-size: cover;
  background-position: center;
  transition: 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 1;
  }

  div {
    position: relative;
    color: white;
    font-size: 1rem;
    font-weight: 600;
    text-align: center;
    z-index: 2;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 7px;

    span {
      font-family: SF Pro Display;
      font-weight: 800;
      font-style: Heavy;
      font-size: 30px;
      line-height: 33px;
      letter-spacing: 0%;
      text-align: center;
      vertical-align: middle;
      color: #FFFFFF;
    }
  }
`;

const InputBox = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1;
`;

const Section = styled.div`
  padding-top: 15px;
`;

const AdditionalService = styled.div`
  padding-top: 10px;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0;

  span {
    color: #6777FF;
  }
`;

const EditButton = styled.button`
  padding: 4px 10px;
  background: #e0e0e0;
  border-radius: 12px;
  border: none;
  font-size: 0.8rem;
  cursor: pointer;
`;

const Agreement = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  font-size: 0.9rem;

  input {
    margin: 0;
  }

  a {
    color: #5c6ef8;
    text-decoration: none;
  }

  /* Mobile: stack as 3 rows (row1 checkbox+text, row2 link, row3 button) */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const Text = styled.span`
  padding: 0 3px 0 10px;
  font-family: SF Pro Display,serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #000000;
  cursor:pointer;

  @media (max-width: 768px) {
    padding: 0 6px 0 10px;
    flex: 1;
  }
`;

const PublicOff = styled.a`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #6777FF;

  @media (max-width: 768px) {
    display: block;
    width: 100%;
    margin-left: 30px; /* align under text (after checkbox) */
  }
`;

const PayButton = styled.button`
  width: 209px;
  height: 46px;
  border-radius: 10px;
  border: none;
  background: #6777FF;
  color: #FFFFFF;
  cursor:pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #6777FF80;
  }

  @media (max-width: 768px) {
    width: 100%;
  }

`;

const PayButtonInner = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
`;

const PaySpinnerWrap = styled.span`
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;

const PlanText = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0%;
  text-align: center;
  color: #FFFFFF;
`;

const NoticeBox = styled.div`
  width: 100%;
  border-radius: 14px;
  padding: 12px 14px;
  background: rgba(226, 229, 255, 1);
  box-shadow: 0px 0px 10px 0px rgba(145, 145, 145, 0.12);
  margin-bottom: 12px;
`;

const NoticeTitle = styled.div`
  font-family: SF Pro Display;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.9);
`;

const NoticeText = styled.div`
  margin-top: 6px;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.7);
`;

const SurchargesList = styled.div``;

const DeliveryTitle = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0%;
  display: flex;
  align-items: center;
  gap: 4px;
  color: #00000080;
`;

const DeliveryBox = styled.div`
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 13px;
  cursor: pointer;
`;

const Box = styled.div`
  display: flex;
  gap: 10px;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0%;
  color: #000000;

  span {
    color: #6777FF;
  }
`;

const Round = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #6777FF;
  appearance: none;
  cursor: pointer;
  position: relative;
  background-color: ${(props) => (props.checked ? "#6777FF" : "#fff")};

  &:checked::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 6px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

// Local-only loader for this FinalPricing block (not fullscreen)
const BlockLoadingOverlay = styled(Loading)`
  border-radius: 16px;
`;

export default FinalPricing;
